/**
 * What each play-space mode asks of a body (D-064(5), architecture §20, GDS §6/§15).
 *
 * Slice D left `requiredCapabilities: []` in the reuse check with a note saying why: refusing the
 * camera outright because a child could not jump today is worse than the substitute the design
 * promises them, and no substitution existed yet. It exists now, so the rule is enforced — and
 * enforcing it means answering one question honestly: *a Compact sitting does not require JUMP.*
 *
 * That is the whole of this file. A mission declares what it needs of a body once
 * (`GameLaunchRequirements.requiredCapabilities`); the room decides how a body may give it. Nothing
 * here knows about missions, rounds or scoring, and nothing here relaxes a requirement — the
 * substitute capability is genuinely required in its place, so a Compact sitting whose calibration
 * never measured an arm raise is refused exactly as a Full-Motion sitting with no jump is.
 */

import type { InputCapability } from '../../shared/types/capabilities';
import type { CameraMode, JumpSubstitute } from '../../shared/types/camera-mode';

/**
 * Which capability a substitute is performed with.
 *
 * All three of GDS §15's substitutes are arm movements, so all three are `HANDS_RAISED`: the
 * calibration step that measures them is the same one (the Compact `reach` step, D-063(1)), and a
 * child who can do one can be asked for another without re-measuring. The function exists anyway
 * because the mapping is a *decision* — if a future substitute were performed with something else,
 * this is the one place that would change, rather than four call sites that each assumed arms.
 */
export function substituteCapabilityFor(_substitute: JumpSubstitute): InputCapability {
  return 'HANDS_RAISED';
}

/** What a counted action (GDS §15's Web Charge) is performed with, in this room. */
export function countActionCapabilityFor(mode: CameraMode): InputCapability {
  return mode === 'compact' ? 'HANDS_RAISED' : 'JUMP';
}

/**
 * A mission's declared capabilities, translated into what *this room* must be able to observe.
 *
 * Full-Motion is the identity translation: the mission is written for jumps and gets them. Compact
 * replaces JUMP with the capability its substitute is performed with, de-duplicated — Spider Math
 * requires HANDS_RAISED already, so a Compact sitting requires two capabilities, not three with one
 * repeated.
 *
 * Order is preserved from the mission's own declaration so that a refusal reads in the order the
 * game asked, and so the result is stable enough to assert on.
 */
export function requiredCameraCapabilities(
  missionCapabilities: readonly InputCapability[],
  mode: CameraMode,
): readonly InputCapability[] {
  const substitute = countActionCapabilityFor(mode);
  const required: InputCapability[] = [];

  for (const capability of missionCapabilities) {
    const translated = capability === 'JUMP' ? substitute : capability;
    if (!required.includes(translated)) required.push(translated);
  }

  return required;
}
